/* eslint-disable no-nested-ternary */
/* eslint-disable no-extra-parens */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import JsonQuery from 'json-query';
import parse from 'parse-json';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import { mergeOptions } from '../GrowthChart/mergeOptions';
import {
    getColorPalette,
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
    getThemeBasedColorPalette,
} from '../utils';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import { sampleData, themeData } from './sampleData';
import ReturnsBarChartStyles from './ReturnsBarChart.styles';

interface highChartOptions {
    path?: string;
    value?: string;
}

export interface ZoneOption {
    year: string;
    color: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    dataKey: string;
    useSampleData: boolean;
    chartTitles: Headings[];
    options: highChartOptions[];
    xLabels: string;
    keys: string;
    formatTo: string;
    dataLabelFormat: string;
    functionToTransform: string;
    transformData?: boolean;
    showDifferentZone: boolean;
    zoneOptions: ZoneOption;
    isStockChart: boolean;
    paletteId?: string;
    typographyClass?: string;
}

const ReturnsBarChart = (props: commonTypes.WidgetInstanceProps) => {
    const { documentType, colorPalettes } = commonHooks.useAppContext();
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        dataKey,
        useSampleData,
        chartTitles,
        options,
        xLabels,
        keys,
        formatTo,
        dataLabelFormat,
        functionToTransform,
        transformData,
        paletteId,
        ...rest
    } = widgetProperties || {};
    const { data } = props;
    const theme = data?.themeSelected;

    const i18n = commonHooks.useLocalization();

    const [highstock, setHighstock] = useState<any>(null);

    useEffect(() => {
        import('highcharts/highstock').then((highstock) => setHighstock(highstock));
    }, []);

    const { value: returnsChartData }: any = JsonQuery(`${dataKey}`, {
        data: useSampleData ? sampleData : data,
    });
    const performanceData = parse(JSON.stringify(returnsChartData || []));

    const _xLabels = i18n.format(xLabels || '', data);
    const _keys = i18n.format(keys || '', data);

    const xAxisLabels: any[] = typeof _xLabels === 'string' ? _xLabels.split(',') : [];
    const mappingKeys: any[] = typeof _keys === 'string' ? _keys.split(',') : [];

    let transformedData = {
        data,
        performanceData,
        showDifferentZone: rest.showDifferentZone,
        zoneOptions: rest.zoneOptions,
        xAxisLabels,
        mappingKeys,
        options,
        showUnderlyingFund: false,
        underlyingFundPath: '',
        xAxisMin: 0,
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedProps =
            transformData &&
            commonUtils.transformDataWithGlobals(transformFunctionName, functionToTransform, {
                ...transformedData,
                JsonQuery,
            });

        if (transformedProps) {
            transformedData = { ...transformedData, ...transformedProps };
        }
    } catch (e) {
        console.log(e);
    }

    if (transformedData.showUnderlyingFund && transformedData.underlyingFundPath) {
        const underlyingData = JsonQuery(`${transformedData.underlyingFundPath}`, { data }).value;
        transformedData.performanceData[0].values = _.mergeWith(
            {},
            transformedData.performanceData?.[0]?.values,
            underlyingData,
            (a, b) => (b === null ? a : undefined)
        );
    }

    const formatValue = (value: any) => {
        let formatType = formatTo;
        if (formatType.endsWith('HideSymbol')) {
            formatType = formatType.replace('HideSymbol', '');
        }

        // Here need to revert French & percentage format because it will be converted
        // to number afterwards and will be passed to chart
        const formattedValue = i18n
            .t(value, formatType || 'decMax1')
            ?.replace(',', '.')
            ?.replace('%', '');

        return typeof formattedValue === 'string' ? parseFloat(formattedValue) : formattedValue;
    };

    const preparedData: any =
        Array.isArray(transformedData.performanceData) &&
        transformedData.performanceData.map((series: any) => {
            const data = {
                name: series.name,
                data: transformedData?.xAxisLabels?.map((_, i: number) => {
                    if (typeof series?.values?.[transformedData?.mappingKeys?.[i]] === 'object') {
                        return {
                            y: formatValue(series?.values?.[transformedData?.mappingKeys?.[i]]?.y),
                            color: series.values[transformedData.mappingKeys[i]]?.color,
                        };
                    } else {
                        return formatValue(series?.values?.[transformedData?.mappingKeys?.[i]]);
                    }
                }),
                type: 'column',
            };
            return data;
        });

    const returnsBarChartId = uuidv4();

    let orderedColors =
        data?.themeSelected && paletteId
            ? getThemeBasedColorPalette(colorPalettes?.[paletteId], data?.colorSpace)
            : getColorPalette();

    // eslint-disable-next-line
    const customColorPallete: string[] = (data as any)?.colorPallete || [];

    if (customColorPallete.length > 0) {
        orderedColors = customColorPallete;
    }

    const zoneIndex = transformedData?.xAxisLabels?.findIndex((x) =>
        x.includes(transformedData?.zoneOptions?.year)
    );

    let highChartOptions: Highcharts.Options = {
        chart: {
            type: 'column',
            height: 300,
        },
        title: {
            text: '',
            align: 'left',
        },
        caption: {
            text: '',
        },
        xAxis: {
            categories: transformedData?.xAxisLabels,
            lineWidth: 0,
            max: transformedData.xAxisLabels.length - 1,
            min: transformedData.xAxisMin,
            plotLines: transformedData?.xAxisLabels.map((_, i) => {
                if (transformedData?.xAxisLabels.length !== i + 1) {
                    return {
                        color: '#D3D3D3',
                        width: 1,
                        value: i + 0.5,
                    };
                } else {
                    return {};
                }
            }),
        },
        yAxis: {
            visible: false,
            allowDecimals: false,
            minPadding: 0.1,
        },
        rangeSelector: {
            enabled: false,
        },
        scrollbar: {
            enabled: false,
        },
        legend: { align: 'left', enabled: false },
        credits: { enabled: false },
        colors: orderedColors,
        plotOptions: {
            column: {
                groupPadding: 0.05,
                pointPadding: 0.01,
                dataLabels: {
                    enabled: true,
                    overflow: 'allow',
                    crop: false,
                    formatter(this: any) {
                        return i18n.format(
                            `{{formatIncludingZero value "${
                                dataLabelFormat || 'decMax1'
                            }" "" "" ""}}`,
                            {
                                value: this.y,
                            }
                        );
                    },
                },
                zoneAxis: 'x',
                zones: [
                    {
                        value: zoneIndex > -1 ? zoneIndex + 1 : undefined,
                        color: rest?.zoneOptions?.color,
                    },
                ],
                minPointLength: 2,
            },
        },
        tooltip: {
            enabled: true,
            formatter(this: any) {
                return `
                    <div>
                        <span style="color:${this.point?.color}">\u25CF</span>
                        ${this.series?.name}: <b>${i18n.format(
                            `{{formatIncludingZero value "${dataLabelFormat || 'decMax1'}" "" "" ""}}`,
                            {
                                value: this.y,
                            }
                        )}</b>
                    </div>
                `;
            },
        },
        navigator: {
            enabled: false,
            xAxis: {
                categories: transformedData?.xAxisLabels,
            },
            series: {
                type: 'column',
            },
        },
        series: preparedData,
    };

    if (theme !== '') {
        const _labels = ['2014', '2015', '2016', '2017', '2018', '2019', '2020', '2021'];
        const themeOptions: any = {
            xAxis: {
                lineWidth: 2,
                lineColor: 'var(--line-color)',
                plotLines: [],
                categories:
                    transformedData?.xAxisLabels.length > 1
                        ? transformedData?.xAxisLabels
                        : _labels,
                max: 7,
                min: 0,
            },
            yAxis: {
                visible: true,
                gridLineColor: 'var(--grid-line-color)',
                title: {
                    text: '',
                },
            },
        };

        if (!dataKey) {
            themeOptions.series = themeData;
        }

        highChartOptions = _.merge(highChartOptions, themeOptions);
    }

    highChartOptions = mergeOptions(
        transformedData.options,
        highChartOptions,
        data,
        i18n,
        props.widgetInstanceId || ''
    );

    return (
        <>
            <div
                className={`returns-bar-chart ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${ReturnsBarChartStyles}${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles || ''}
                `}
            >
                <HeaderWrapper headings={chartTitles} data={data} />
                <div id={`${returnsBarChartId}`} className="js-returns-bar-chart-tile">
                    {['pdf', 'pptx'].includes(documentType) ? (
                        <div id={`${returnsBarChartId}-returns-bar-chart`} />
                    ) : rest.isStockChart ? (
                        highstock && (
                            <HighchartsReact highcharts={highstock} options={highChartOptions} />
                        )
                    ) : (
                        <HighchartsReact highcharts={Highcharts} options={highChartOptions} />
                    )}
                </div>
            </div>
            {['pdf', 'pptx'].includes(documentType) && (
                <>
                    <script
                        id={`${returnsBarChartId}-chart-options`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(highChartOptions),
                        }}
                    ></script>
                    <script
                        id={`${returnsBarChartId}-json-data`}
                        type="text/json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(preparedData) }}
                    ></script>
                </>
            )}
        </>
    );
};

export default ReturnsBarChart;
